import * as Sentry from '@sentry/browser'
import React, { useEffect, FormEvent, useState } from 'react'
import { flowRight as compose, isEmpty } from 'lodash'
import { RouteChildrenProps } from 'react-router-dom'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useRecoilState, useSetRecoilState } from 'recoil'
import { useTranslation } from 'react-i18next'
import { getPhoneFormatter } from 'helpers/common'

import {
  BranchForm,
  withBranch,
  schemaBranchFormUpdate,
  initialBranchFormUpdate,
  BranchService,
  renderBreadcrumbsEdit,
} from 'features/Branch'
import { Button, PromptGuard, Modal } from 'components'
import { Confirmation, ModalSuccess } from 'components/Modal'
import { withBackOfficeCms } from 'helpers/hocs'
import { routes } from 'helpers/routes'
import { Address, BranchForForm, CONTACT_TYPE, BranchForApiSubmit } from 'types'
import { formatForm } from 'features/Branch/format'
import { loaderState } from 'recoils/atoms'
import {
  useBreadcrumbs,
  useFormSubmitLoader,
  usePromptForm,
} from 'helpers/hooks'
import { branchFormState } from 'recoils/atoms/branchFormAtom'

const BranchEdit = ({
  history,
  match,
}: RouteChildrenProps<{ id: string }>): JSX.Element => {
  const id = match?.params.id || '0'

  const [{ data: branch, loading: findOneLoading }] =
    BranchService.useFindOneById(id)
  const methods = useForm<BranchForForm>({
    defaultValues: initialBranchFormUpdate,
    resolver: yupResolver(schemaBranchFormUpdate),
  })
  const [shouldPrompt, setShouldPrompt] = usePromptForm<BranchForForm>(methods)
  const [isLoadingForm] = useFormSubmitLoader<BranchForForm>(methods)
  const setLoader = useSetRecoilState(loaderState)
  const [branchForm, setBranchForm] = useRecoilState(branchFormState)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isOpenConfirm, setIsOpenConfirm] = useState<boolean>(false)
  const [isOpenSuccess, setIsOpenSuccess] = useState<boolean>(false)
  const [payload, setPayload] = useState<BranchForApiSubmit>()
  const { t } = useTranslation(['common', 'layout'])

  useEffect(() => {
    if (shouldPrompt || methods.formState.isSubmitted) {
      return
    }

    if (methods.formState.isDirty) {
      setShouldPrompt(true)
    }
  }, [methods.formState])

  useBreadcrumbs(renderBreadcrumbsEdit(id))

  const callApi = async () => {
    if (payload) {
      try {
        setLoader(true)

        await BranchService.update(id, payload, handleOpenSuccess)
        setLoader(false)
      } catch (error) {
        Sentry.captureException(error)
        setLoader(false)
      }
    }
  }

  const beforeSubmit = (e: FormEvent<HTMLFormElement>) => {
    methods.setValue('currentState', 'onSubmit')
    methods.handleSubmit(setData)(e)
    methods.setValue('currentState', '')
  }

  const setAddressLoading = (loading: boolean) => {
    setIsLoading(loading)
  }

  const handleBack = (): void => {
    history.push(routes.branch.path)
  }

  const setData = (data: BranchForForm) => {
    const payload = formatForm(data)
    if (isEmpty(methods.errors) && payload) {
      setIsOpenConfirm(true)
      setPayload(payload)
    }
  }

  const renderConfirmModal = () => {
    return (
      isOpenConfirm && (
        <Modal
          isIcon={false}
          isOpen={isOpenConfirm}
          onClose={() => setIsOpenConfirm(false)}
        >
          <Confirmation
            title={t('common:confirmation.title.label')}
            description={t('common:confirmation.description.label')}
            confirmText={t('common:save.button')}
            cancelText={t('common:cancel.button')}
            onConfirm={callApi}
            onClose={() => setIsOpenConfirm(false)}
          />
        </Modal>
      )
    )
  }

  const renderSuccessModal = () => {
    return (
      isOpenSuccess && (
        <ModalSuccess
          isOpen={isOpenSuccess}
          description={t('common:success.label')}
          onClose={() => setIsOpenSuccess(false)}
        />
      )
    )
  }

  const handleOpenSuccess = async () => {
    setIsOpenSuccess(true)

    await setTimeout(() => {
      setShouldPrompt(false)
      handleBack()
    }, 2000)
  }

  useEffect(() => {
    setLoader(findOneLoading)
  }, [findOneLoading])

  useEffect(() => {
    if (!branch) {
      return
    }

    const emailContact = branch.contacts.find(
      (contact) => contact.type === CONTACT_TYPE.EMAIL && id === contact.refId,
    )
    const phoneContact = branch.contacts.find(
      (contact) => contact.type === CONTACT_TYPE.PHONE && id === contact.refId,
    )
    const contactPhoneContact = branch.contacts.find(
      (contact) =>
        contact.type === CONTACT_TYPE.CONTACT_PHONE && id === contact.refId,
    )
    const addressData: Address = branch.addresses[0]

    methods.reset({
      ...initialBranchFormUpdate,
      isActive: branch.isActive,
      code: branch.code || '',
      companyId: branch.companyId || '',
      name: branch.name || '',
      countryCode: phoneContact?.countryCode || '+66',
      phoneNumber: getPhoneFormatter(`${phoneContact?.number}`) || '',

      isSameAsCompany: branch.isSameAsCompany,
      address: addressData.address || '',
      postalCode: addressData.postalCode || '',
      email: emailContact?.email || '',
      location: branch.location || '',
      detail: branch.detail || '',

      contactName: branch.contactName || '',
      contactCountryCode: contactPhoneContact?.countryCode || '+66',
      contactPhoneNumber:
        getPhoneFormatter(`${contactPhoneContact?.number}`) || '',
    })

    setBranchForm({
      ...branchForm,
      companyName: branch.companyName,
      branchFormEditAddress: addressData,
    })
  }, [branch])

  return (
    <div className='container__form'>
      <FormProvider {...methods}>
        <PromptGuard when={shouldPrompt} />
        <form onSubmit={beforeSubmit}>
          <BranchForm setAddressLoading={setAddressLoading} />

          <div className='container__form__actions'>
            <Button variants='secondary' onClick={handleBack}>
              {t('common:cancel.button')}
            </Button>
            <Button type='submit' isLoading={isLoading || isLoadingForm}>
              {t('common:save.button')}
            </Button>
          </div>
        </form>
      </FormProvider>

      {renderConfirmModal()}
      {renderSuccessModal()}
    </div>
  )
}

export default compose(withBackOfficeCms, withBranch)(BranchEdit)
